import { useCallback, useEffect, useState } from 'react';

const KEY = 'ofw_history';
const EVENT = 'ofw-history-change';

export interface HistoryEntry {
  id: string;
  episode: number;
  progress: number;
  updatedAt: number;
}

function readHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as HistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function writeHistory(list: HistoryEntry[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 30)));
  } catch {
    /* storage unavailable, ignore */
  }
  window.dispatchEvent(new Event(EVENT));
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>(() => readHistory());

  useEffect(() => {
    const handler = () => setHistory(readHistory());
    window.addEventListener(EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const recordWatch = useCallback((id: string, episode: number, progress: number) => {
    const current = readHistory();
    const rest = current.filter((item) => item.id !== id);
    const entry: HistoryEntry = { id, episode, progress, updatedAt: Date.now() };
    writeHistory([entry, ...rest]);
  }, []);

  const getEntry = useCallback(
    (id: string): HistoryEntry | undefined => history.find((item) => item.id === id),
    [history],
  );

  const clearHistory = useCallback(() => writeHistory([]), []);

  return { history, recordWatch, getEntry, clearHistory };
}